import { Autocomplete, Box, Button, TextField } from '@mui/material';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBulkDesignTag } from '~/api/design-tags/bulk';
import { Dialog, DialogTitle } from '~/components/dialogs/components';
import { DialogActions, DialogContent } from '~/components/dialogs/lib/styles';
import { assert } from '~/lib/assert';

export interface BulkTagDialogProps {
  designIds: string[];
  designTags: string[];
  onClose: () => void;
  onComplete: () => void;
  open: boolean;
  tags: string[];
}

export const BulkTagDialog = ({
  designIds,
  designTags,
  onClose,
  onComplete,
  open,
  tags,
}: BulkTagDialogProps) => {
  const { t } = useTranslation();
  const [selectedTag, setSelectedTag] = useState<string | null>(null);

  const [bulkTag, { called }] = useBulkDesignTag();

  const handleClose = useCallback(() => {
    setSelectedTag(() => null);
    onClose();
  }, [onClose]);

  return (
    <Dialog maxWidth="xs" fullWidth onClose={handleClose} open={open}>
      <DialogTitle onClose={handleClose}>{t('pages.studio.dialogs.add_tag.title')}</DialogTitle>

      <DialogContent>
        <Box display="flex" alignItems="center" justifyContent="center" padding={2}>
          <Autocomplete
            freeSolo
            fullWidth
            onChange={(_event, option) => setSelectedTag(option ?? null)}
            onInputChange={(_, value) => setSelectedTag(value)}
            options={designTags}
            renderInput={(params) => (
              <TextField
                {...params}
                autoFocus
                label={t('common.tags.choose_tag')}
                variant="outlined"
              />
            )}
            value={selectedTag || null}
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={handleClose}>
          {t('common.dialogs.cancel')}
        </Button>
        <Button
          color="primary"
          variant="contained"
          disabled={called || !selectedTag}
          onClick={async () => {
            assert(selectedTag != null, 'tag is null');
            await bulkTag({
              variables: {
                items: designIds.map((x) => {
                  return {
                    taggableId: Number(x),
                    taggableType: 'DESIGN',
                  };
                }),
                tag: selectedTag,
                tags,
              },
            });
            handleClose();
            onComplete();
          }}
        >
          {t('common.dialogs.add')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
